import React, { useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import SectionValueCalculator from '../PageBuilder/SectionBuilder/SectionValueCalculator';
import SectionEmotoHero from '../PageBuilder/SectionBuilder/SectionEmotoHero';
import SectionOurStory from '../PageBuilder/SectionBuilder/SectionOurStory';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Anchor id of the value calculator. The hero links to it, so it is shared rather than
// written out twice.
const CALCULATOR_SECTION_ID = 'emoto-value-calculator';

// Local (non-CMS) sections. These are registered through
// `options.sectionComponents` below, which SectionBuilder merges on top of its
// own defaultSectionComponents map, keyed by `sectionType`.
const HERO_SECTION = {
  sectionType: 'emotoHero',
  sectionId: 'emoto-hero',
  // `listingSelection` is what makes a section eligible for the shared featured-listings
  // fetch (src/ducks/featuredListings.duck.js). The hero uses it to show the newest
  // listings; it is not a CMS 'listings' section.
  listingSelection: 'newest',
  // Any extra key on a section object is spread onto the section component by
  // SectionBuilder, which is how the hero learns where its CTA should scroll.
  valueCalculatorSectionId: CALCULATOR_SECTION_ID,
};

const CALCULATOR_SECTION = {
  sectionType: 'valueCalculator',
  sectionId: CALCULATOR_SECTION_ID,
};

const STORY_SECTION_ID = 'our-story';

const STORY_SECTION = {
  sectionType: 'ourStory',
  sectionId: STORY_SECTION_ID,
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const assetData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // The landing page is composed of local sections: hero -> value calculator -> our story.
  //
  // Note 1: CMS sections from the hosted landing-page asset are deliberately NOT rendered.
  // The asset still holds the old marketing sections ("Welcome to UsedEmoto!", "Why Choose
  // UsedEmoto?", "How UsedEmoto Works") and the page is meant to be the emoto hero, the
  // calculator, and the dedicated Our Story section.
  // Note 2: `meta` is still taken from the asset, so Console keeps control of the page's
  // SEO title/description.
  // Note 3: the only case where we pass the falsy asset through is a *failed* fetch, because
  // PageBuilder then renders the FallbackPage (maintenance copy), which is more informative
  // than a bare hero.
  const pageDataWithLocalSections = useMemo(() => {
    const hasFailedWithoutData = !assetData && !!error;
    if (hasFailedWithoutData) return assetData;
    return {
      ...(assetData || {}),
      sections: [HERO_SECTION, CALCULATOR_SECTION, STORY_SECTION],
    };
  }, [assetData, error]);

  return (
    <PageBuilder
      pageAssetsData={pageDataWithLocalSections}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      options={{
        sectionComponents: {
          emotoHero: { component: SectionEmotoHero },
          valueCalculator: { component: SectionValueCalculator },
          ourStory: { component: SectionOurStory },
        },
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
